import React, { useEffect, useState, useCallback } from 'react';
import { labelAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
    Plus, 
    Search, 
    Building2,
    Loader2,
    Edit2,
    Trash2,
    Mail,
    Phone,
    Music,
    Percent,
    AlertCircle,
    CreditCard,
    FileText,
    User,
    MapPin,
    Landmark
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';

const LabelsPage = () => {
    const { user } = useAuth();
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLabel, setEditingLabel] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        email: '',
        contact_person: '',
        phone: '',
        address: '',
        revenue_share_percentage: 70.0,
        agreement_date: '',
        agreement_renewal_date: '',
        // Registration details
        registered_name: '',
        registered_address: '',
        pan_number: '',
        tan_number: '',
        gst_number: '',
        // Contact person details
        contact_person_email: '',
        contact_person_phone: '',
        // Bank details
        bank_account_name: '',
        bank_account_number: '',
        bank_ifsc_code: '',
        bank_account_type: '',
        bank_name: '',
        bank_branch: '',
        // Premium fields
        bio: '',
        profile_image: '',
        cover_image: '',
        website: '',
        youtube_url: '',
        instagram_url: '',
        spotify_url: '',
        twitter_url: '',
        featured_video_url: ''
    });

    const fetchLabels = useCallback(async () => {
        try {
            const response = await labelAPI.getAll();
            setLabels(response.data);
        } catch (error) {
            console.error('Failed to fetch labels:', error);
            toast.error('Failed to load labels');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLabels();
    }, [fetchLabels]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingLabel) {
                await labelAPI.update(editingLabel.id, formData);
                toast.success('Label updated successfully');
            } else {
                await labelAPI.create(formData);
                toast.success('Label created successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchLabels();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save label');
        }
    };

    const handleEdit = (label) => {
        setEditingLabel(label);
        setFormData({
            name: label.name,
            code: label.code,
            email: label.email,
            contact_person: label.contact_person,
            phone: label.phone || '',
            address: label.address || '',
            revenue_share_percentage: label.revenue_share_percentage || 70.0,
            agreement_date: label.agreement_date || '',
            agreement_renewal_date: label.agreement_renewal_date || '',
            // Registration details
            registered_name: label.registered_name || '',
            registered_address: label.registered_address || '',
            pan_number: label.pan_number || '',
            tan_number: label.tan_number || '',
            gst_number: label.gst_number || '',
            // Contact person details
            contact_person_email: label.contact_person_email || '',
            contact_person_phone: label.contact_person_phone || '',
            // Bank details
            bank_account_name: label.bank_account_name || '',
            bank_account_number: label.bank_account_number || '',
            bank_ifsc_code: label.bank_ifsc_code || '',
            bank_account_type: label.bank_account_type || '',
            bank_name: label.bank_name || '',
            bank_branch: label.bank_branch || '',
            // Premium fields
            bio: label.bio || '',
            profile_image: label.profile_image || '',
            cover_image: label.cover_image || '',
            website: label.website || '',
            youtube_url: label.youtube_url || '',
            instagram_url: label.instagram_url || '',
            spotify_url: label.spotify_url || '',
            twitter_url: label.twitter_url || '',
            featured_video_url: label.featured_video_url || ''
        });
        setDialogOpen(true);
    };

    const handleDelete = async (labelId) => {
        if (!window.confirm('Are you sure you want to delete this label?')) return;
        
        try {
            await labelAPI.delete(labelId);
            toast.success('Label deleted successfully');
            fetchLabels();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete label');
        }
    };

    const handleApproveRevenueShare = async (labelId, action) => {
        try {
            await labelAPI.approveRevenueShare(labelId, action);
            toast.success(`Revenue share change ${action}d`);
            fetchLabels();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to ${action} revenue share`);
        }
    };

const handleDocumentDownload = async (doc) => {
    if (!editingLabel) return;

    try {
        const response = await labelAPI.downloadDocument(
            editingLabel.id,
            doc.id
        );

        const blobUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');

        link.href = blobUrl;
        link.download = doc.filename || 'document';

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
        toast.error(
            error.response?.data?.detail ||
            'Failed to download document'
        );
    }
};

const handleDocumentDelete = async (doc) => {
    if (!editingLabel) return;

    const confirmed = window.confirm(
        `Delete "${doc.filename}"?`
    );

    if (!confirmed) return;

    try {
        await labelAPI.deleteDocument(
            editingLabel.id,
            doc.id
        );

        const refreshed = await labelAPI.getOne(editingLabel.id);
        setEditingLabel(refreshed.data);

        toast.success('Document deleted');
    } catch (error) {
        toast.error(
            error.response?.data?.detail ||
            'Failed to delete document'
        );
    }
};

    const resetForm = () => {
        setEditingLabel(null);
        setFormData({
            name: '',
            code: '',
            email: '',
            contact_person: '',
            phone: '',
            address: '',
            revenue_share_percentage: 70.0,
            agreement_date: '',
            agreement_renewal_date: '',
            // Registration details
            registered_name: '',
            registered_address: '',
            pan_number: '',
            tan_number: '',
            gst_number: '',
            // Contact person details
            contact_person_email: '',
            contact_person_phone: '',
            // Bank details
            bank_account_name: '',
            bank_account_number: '',
            bank_ifsc_code: '',
            bank_account_type: '',
            bank_name: '',
            bank_branch: '',
            // Premium fields
            bio: '',
            profile_image: '',
            cover_image: '',
            website: '',
            youtube_url: '',
            instagram_url: '',
            spotify_url: '',
            twitter_url: '',
            featured_video_url: ''
        });
    };

    const filteredLabels = labels.filter(label =>
        label.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        label.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        label.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="labels-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Record Labels</h1>
                    <p className="text-muted-foreground">Manage your partner record labels</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="bg-primary hover:bg-primary/90" data-testid="add-label-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Label
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border/40 max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingLabel ? 'Edit Label' : 'Add New Label'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-6 py-4">
                            {/* Basic Info Section */}
                            <div>
                                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    Basic Information
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Label Name *</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                            required
                                            className="bg-background/50"
                                            data-testid="label-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Label Code *</Label>
                                        <Input
                                            value={formData.code}
                                            onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                                            required
                                            placeholder="e.g., FLAME"
                                            className="bg-background/50 font-mono"
                                            data-testid="label-code-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email *</Label>
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            required
                                            className="bg-background/50"
                                            data-testid="label-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Contact Person *</Label>
                                        <Input
                                            value={formData.contact_person}
                                            onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                                            required
                                            className="bg-background/50"
                                            data-testid="label-contact-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Phone</Label>
                                        <Input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Revenue Share % *</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={formData.revenue_share_percentage}
                                            onChange={(e) => setFormData({...formData, revenue_share_percentage: parseFloat(e.target.value)})}
                                            required
                                            className="bg-background/50"
                                            data-testid="revenue-share-input"
                                        />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label>Address</Label>
                                        <Input
                                            value={formData.address}
                                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Agreement Date</Label>
                                        <Input
                                            type="date"
                                            value={formData.agreement_date}
                                            onChange={(e) => setFormData({...formData, agreement_date: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Agreement Renewal Date</Label>
                                        <Input
                                            type="date"
                                            value={formData.agreement_renewal_date}
                                            onChange={(e) => setFormData({...formData, agreement_renewal_date: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Registration Details Section */}
                            <div className="border-t border-border/40 pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Registration Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2 col-span-2">
                                        <Label>Name as per Registration</Label>
                                        <Input
                                            value={formData.registered_name}
                                            onChange={(e) => setFormData({...formData, registered_name: e.target.value})}
                                            placeholder="Legal entity name"
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label>Registered Address</Label>
                                        <Input
                                            value={formData.registered_address}
                                            onChange={(e) => setFormData({...formData, registered_address: e.target.value})}
                                            placeholder="Full registered address"
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>PAN Number</Label>
                                        <Input
                                            value={formData.pan_number}
                                            onChange={(e) => setFormData({...formData, pan_number: e.target.value.toUpperCase()})}
                                            placeholder="AAAAA0000A"
                                            maxLength={10}
                                            className="bg-background/50 font-mono"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>TAN Number</Label>
                                        <Input
                                            value={formData.tan_number}
                                            onChange={(e) => setFormData({...formData, tan_number: e.target.value.toUpperCase()})}
                                            placeholder="AAAA00000A"
                                            maxLength={10}
                                            className="bg-background/50 font-mono"
                                        />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label>GST Number (if applicable)</Label>
                                        <Input
                                            value={formData.gst_number}
                                            onChange={(e) => setFormData({...formData, gst_number: e.target.value.toUpperCase()})}
                                            placeholder="00AAAAA0000A0A0"
                                            maxLength={15}
                                            className="bg-background/50 font-mono"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contact Person Details Section */}
                            <div className="border-t border-border/40 pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    Contact Person Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Contact Person Email</Label>
                                        <Input
                                            type="email"
                                            value={formData.contact_person_email}
                                            onChange={(e) => setFormData({...formData, contact_person_email: e.target.value})}
                                            placeholder="contact@label.com"
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Contact Person Phone</Label>
                                        <Input
                                            type="tel"
                                            value={formData.contact_person_phone}
                                            onChange={(e) => setFormData({...formData, contact_person_phone: e.target.value})}
                                            placeholder="+91 XXXXXXXXXX"
                                            className="bg-background/50"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bank Details Section */}
                            <div className="border-t border-border/40 pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                    <Landmark className="w-4 h-4" />
                                    Bank Account Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Account Holder Name</Label>
                                        <Input
                                            value={formData.bank_account_name}
                                            onChange={(e) => setFormData({...formData, bank_account_name: e.target.value})}
                                            placeholder="As per bank records"
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Number</Label>
                                        <Input
                                            value={formData.bank_account_number}
                                            onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                                            placeholder="Account number"
                                            className="bg-background/50 font-mono"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>IFSC Code</Label>
                                        <Input
                                            value={formData.bank_ifsc_code}
                                            onChange={(e) => setFormData({...formData, bank_ifsc_code: e.target.value.toUpperCase()})}
                                            placeholder="AAAA0000000"
                                            maxLength={11}
                                            className="bg-background/50 font-mono"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Type</Label>
                                        <Select 
                                            value={formData.bank_account_type} 
                                            onValueChange={(value) => setFormData({...formData, bank_account_type: value})}
                                        >
                                            <SelectTrigger className="bg-background/50">
                                                <SelectValue placeholder="Select type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="savings">Savings</SelectItem>
                                                <SelectItem value="current">Current</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bank Name</Label>
                                        <Input
                                            value={formData.bank_name}
                                            onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                                            placeholder="Bank name"
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Branch</Label>
                                        <Input
                                            value={formData.bank_branch}
                                            onChange={(e) => setFormData({...formData, bank_branch: e.target.value})}
                                            placeholder="Branch name"
                                            className="bg-background/50"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Premium/Branding Section */}
                            <div className="border-t border-border/40 pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                    <Music className="w-4 h-4" />
                                    Branding & Media (Optional)
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2 col-span-2">
                                        <Label>Bio / Description</Label>
                                        <Input
                                            value={formData.bio}
                                            onChange={(e) => setFormData({...formData, bio: e.target.value})}
                                            placeholder="Brief description of the label/artist"
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Profile Image URL</Label>
                                        <Input
                                            value={formData.profile_image}
                                            onChange={(e) => setFormData({...formData, profile_image: e.target.value})}
                                            placeholder="https://..."
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Cover Image URL</Label>
                                        <Input
                                            value={formData.cover_image}
                                            onChange={(e) => setFormData({...formData, cover_image: e.target.value})}
                                            placeholder="https://..."
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Website</Label>
                                        <Input
                                            value={formData.website}
                                            onChange={(e) => setFormData({...formData, website: e.target.value})}
                                            placeholder="https://..."
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>YouTube URL</Label>
                                        <Input
                                            value={formData.youtube_url}
                                            onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                                            placeholder="https://youtube.com/..."
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Instagram URL</Label>
                                        <Input
                                            value={formData.instagram_url}
                                            onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                                            placeholder="https://instagram.com/..."
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Spotify URL</Label>
                                        <Input
                                            value={formData.spotify_url}
                                            onChange={(e) => setFormData({...formData, spotify_url: e.target.value})}
                                            placeholder="https://open.spotify.com/..."
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label>Featured Video (YouTube Embed URL)</Label>
                                        <Input
                                            value={formData.featured_video_url}
                                            onChange={(e) => setFormData({...formData, featured_video_url: e.target.value})}
                                            placeholder="https://www.youtube.com/embed/VIDEO_ID"
                                            className="bg-background/50"
                                        />
                                    </div>
                                </div>
                            </div>

                            {editingLabel?.documents?.length > 0 && (
                                <div className="border-t border-border/40 pt-6">
                                    <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        Documents
                                    </h3>

                                    <div className="space-y-3">
                                        {editingLabel.documents.map((doc) => (
                                            <div
                                                key={doc.id}
                                                className="flex items-center justify-between rounded-lg border border-border/40 p-3"
                                            >
                                                <div>
                                                    <p className="text-sm font-medium">
                                                        {doc.filename}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {doc.document_type?.replaceAll('_', ' ')}
                                                    </p>
                                                </div>

                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleDocumentDownload(doc)}
                                                    >
                                                        Download
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() => handleDocumentDelete(doc)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}


                            <div className="flex justify-end gap-3 pt-4 border-t border-border/40">
                                <Button type="button" variant="outline" onClick={() => {
                                    setDialogOpen(false);
                                    resetForm();
                                }}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-label-btn">
                                    {editingLabel ? 'Update Label' : 'Create Label'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search */}
            <Card className="bg-card/50 border-border/40">
                <CardContent className="p-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search labels..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 bg-background/50"
                            data-testid="search-labels-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Labels Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLabels.length === 0 ? (
                    <Card className="col-span-full bg-card/50 border-border/40">
                        <CardContent className="py-12 text-center">
                            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-muted-foreground">No labels found</p>
                        </CardContent>
                    </Card>
                ) : (
                    filteredLabels.map((label) => (
                        <Card key={label.id} className="bg-card/50 border-border/40 hover:border-primary/30 transition-all duration-300" data-testid={`label-card-${label.code}`}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                                            <Building2 className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-heading">{label.name}</CardTitle>
                                            <p className="text-xs font-mono text-muted-foreground">{label.code}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleEdit(label)}
                                            className="h-8 w-8"
                                            data-testid={`edit-label-${label.code}`}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(label.id)}
                                            className="h-8 w-8 text-destructive hover:text-destructive"
                                            data-testid={`delete-label-${label.code}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Mail className="w-4 h-4" />
                                    <span className="truncate">{label.email}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className="font-medium">{label.contact_person}</span>
                                </div>
                                {label.phone && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Phone className="w-4 h-4" />
                                        <span>{label.phone}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-sm">
                                    <Percent className="w-4 h-4 text-primary" />
                                    <span className="font-medium">{label.revenue_share_percentage || 70}% Revenue Share</span>
                                    {label.pending_revenue_share && (
                                        <div className="ml-2 flex items-center gap-2">
                                            <Badge variant="outline" className="bg-yellow-500/20 text-yellow-500 border-yellow-500/20">
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                {label.pending_revenue_share}% Pending
                                            </Badge>
                                            {(user?.role === 'admin' || user?.role === 'founder') && (
                                                <div className="flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-6 px-2 text-xs text-secondary hover:text-secondary"
                                                        onClick={() => handleApproveRevenueShare(label.id, 'approve')}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                                                        onClick={() => handleApproveRevenueShare(label.id, 'reject')}
                                                    >
                                                        Reject
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {(label.agreement_date || label.agreement_renewal_date) && (
                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs">Agreement:</span>
                                            <span>{label.agreement_date || '-'}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs">Renewal:</span>
                                            <span>{label.agreement_renewal_date || '-'}</span>
                                        </div>
                                    </div>
                                )}
                                <div className="pt-3 border-t border-border/40 flex justify-between text-sm">
                                    <div className="flex items-center gap-1.5">
                                        <Music className="w-4 h-4 text-primary" />
                                        <span className="font-medium">{label.total_songs}</span>
                                        <span className="text-muted-foreground">songs</span>
                                    </div>
                                    <div className="text-secondary font-mono font-medium">
                                        {formatCurrency(label.total_revenue)}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
};

export default LabelsPage;
